import { useCallback, useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useGlobalErrorStore } from 'scbk-services';
import { Dialog, useTokensStore } from 'scbk-shared-module';
import {
  useAutVrfAddActivate,
  useAutVrfAddApplyARS,
  useAutVrfAddCreateARSAuthNumber,
  useAutVrfAddRegistDeviceAuth,
} from 'scbk-shared-module/modules/Additional/hooks/api/autVrfAdditionalHooks';
import { AutVrfAddActivateResponse } from 'scbk-shared-module/modules/Additional/types/api/autVrfAddActivate';
import { AutVrfAddApplyARSRequest } from 'scbk-shared-module/modules/Additional/types/api/autVrfAddApplyARS';
import { ConfirmBox, InfoText } from 'scbk-ui-web';
import { useDeviceAuthStore } from '../stores/useDeviceAuthStore';

interface Props {
  trigger: number;
  onComplete: () => void;
}

const DeviceAuthStep4Component: React.FC<Props> = ({ trigger, onComplete }) => {
  const [authNum, setAuthNum] = useState('');

  // 다국어 처리
  const { t } = useTranslation();

  const { authTelNo, deviceAlias, allowOtherDeviceYn, additionalInfo, setAdditionalInfo } =
    useDeviceAuthStore();
  const { reset: resetTokens } = useTokensStore();

  const { request: createARSAuthNumber } = useAutVrfAddCreateARSAuthNumber({
    onError: (error) => {
      useGlobalErrorStore.getState().setErrorInfo({ ...error, title: `!!!!${error.code}` });
    },
  });

  const execCreateARSAuthNumber = useCallback(async () => {
    const result = await createARSAuthNumber();

    if (result?.authNumber) {
      return result.authNumber;
    } else {
      console.log('error, ', result);
    }

    return null;
  }, []);

  const { request: activate } = useAutVrfAddActivate({
    onError: (error) => {
      useGlobalErrorStore.getState().setErrorInfo({ ...error, title: `!!!!${error.code}` });
    },
  });

  const execActivate = useCallback(async () => {
    const activateResult: AutVrfAddActivateResponse | undefined = await activate({
      additionalType: 'D',
      transType: 'PCFIX_0100',
    });

    if (!activateResult) return;

    setAdditionalInfo(activateResult);

    const createResult = await execCreateARSAuthNumber();

    if (!createResult) return;

    setAuthNum(createResult);
  }, []);

  const { requestWithSecure: registDeviceAuth } = useAutVrfAddRegistDeviceAuth({
    onError: (error) => {
      useGlobalErrorStore.getState().setErrorInfo({ ...error, title: `!!!!${error.code}` });
    },
  });

  const { request: applyARS } = useAutVrfAddApplyARS({
    onError: (error) => {
      useGlobalErrorStore.getState().setErrorInfo({ ...error, title: `!!!!${error.code}` });
    },
  });

  const execApplyARS = useCallback(async () => {
    const request: AutVrfAddApplyARSRequest = {
      // NONPC_0301
      transType: 'PCFIX_0100',
      authTelNo: authTelNo ?? '',
      account: 'SCFBIB',
      wasTranNo: additionalInfo?.wasTranNo ?? '',
    };

    //   doNProgBackgroundService('1');

    const result = await applyARS(request);

    //   doNProgBackgroundService('2');

    if (result?.resultCode === '0000') {
      const res = await registDeviceAuth({
        allowOtherDeviceYn,
        deviceAlias,
        authTelNo,
      });

      if (!res) return;

      resetTokens();

      const isOk = await Dialog.alert({
        content: t('AdditionalComponent.114', 'PC지정이 완료되었어요.'),
      });

      if (isOk) {
        onComplete?.();
      }
    } else {
      const errorMap: Record<string, { code: string; message: string }> = {
        '4003': {
          code: 'AdditionalComponent.083',
          message: '입력시간이 초과되었습니다. 다시 인증 요청하시기 바랍니다.',
        },
        '4011': {
          code: 'AdditionalComponent.084',
          message:
            '현재 고객님께서 통화중임으로 인증이 취소되었습니다. 다시 인증 요청하시기 바랍니다.',
        },
        '4012': {
          code: 'AdditionalComponent.085',
          message:
            '전화번호가 올바르지 않거나, 인증을 위한 필요정보가 없습니다. 다시 확인하시기 바랍니다.',
        },
        '4014': {
          code: 'AdditionalComponent.083',
          message: '입력시간이 초과되었습니다. 다시 인증 요청하시기 바랍니다.',
        },
        '4017': {
          code: 'AdditionalComponent.086',
          message: '입력하신 인증번호가 일치하지 않습니다.',
        },
        '4018': {
          code: 'AdditionalComponent.087',
          message: '고객님과 전화연결이 중단되었습니다. 다시 인증요청 하시기 바랍니다.',
        },
        '4019': {
          code: 'AdditionalComponent.088',
          message:
            '현재 고객님께서 통화중이시거나, 음성사서함으로 연결되어 인증이 취소되었습니다. 다시 인증요청 하시기 바랍니다.',
        },
        '4021': {
          code: 'AdditionalComponent.089',
          message: '통신사 전화량이 많아 연결이 원활하지 않습니다. 다시 인증요청하시기 바랍니다.',
        },
        '4022': {
          code: 'AdditionalComponent.090',
          message: '통신사 시스템 장애로 연결이 되지 않습니다. 잠시 후 다시 거래하시 기 바랍니다.',
        },
        '4101': {
          code: 'AdditionalComponent.085',
          message:
            '전화번호가 올바르지 않거나, 인증을 위한 필요정보가 없습니다. 다시 확인하시기 바랍니다.',
        },
        '4103': {
          code: 'AdditionalComponent.085',
          message:
            '전화번호가 올바르지 않거나, 인증을 위한 필요정보가 없습니다. 다시 확인하시기 바랍니다.',
        },
        '4104': {
          code: 'AdditionalComponent.085',
          message:
            '전화번호가 올바르지 않거나, 인증을 위한 필요정보가 없습니다. 다시 확인하시기 바랍니다.',
        },
        '4105': {
          code: 'AdditionalComponent.089',
          message: '통신사 전화량이 많아 연결이 원활하지 않습니다. 다시 인증요청하시기 바랍니다.',
        },
        default: {
          code: 'AdditionalComponent.091',
          message: '통신연결이 원활하지 않습니다. 잠시 후 다시 거래하시기 바랍니다.',
        },
      };

      const errorCode = result?.resultCode ?? 'default';

      const { code, message } = errorMap[errorCode] ?? errorMap.default;

      await Dialog.alert({
        content: t(code, message),
        onClick: () => {
          //TODO: 팝업닫기
        },
      });
    }
  }, [authTelNo, additionalInfo]);

  useEffect(() => {
    if (trigger === 0) return;
    execApplyARS();
  }, [trigger]);

  const isMount = useRef(false);
  useEffect(() => {
    if (isMount.current) return;
    execActivate();
    isMount.current = true;
  }, []);

  return (
    <>
      <h2 className="page-heading-title mb-12">
        <Trans
          t={t}
          i18nKey="AdditionalComponent.044"
          components={[<span className="block md:inline md:mr-1"></span>]}
        >
          <span className="block md:inline md:mr-1">전화요청 후 화면에 표시된</span>
          인증번호를 입력해 주세요
        </Trans>
      </h2>
      <dl className="flex flex-col gap-2 text-center mb-5">
        <dt className="body-md text-basic-04">{t('AdditionalComponent.045', '수신 전화번호')}</dt>
        <dd className="body-lg font-weight-medium text-basic-02">{authTelNo}</dd>
      </dl>
      <ConfirmBox>{authNum}</ConfirmBox>
      <InfoText
        type="primary"
        text={t(
          'AdditionalComponent.046',
          '전화요청을 했는데 전화가 오지 않으면, 다시 시도해 주세요. 계속 연결이 되지 않으면 1588-1599가 착신 금지되어 있는지 확인해 주세요.',
        )}
        className="mt-3"
      />
    </>
  );
};

export default DeviceAuthStep4Component;
